import React, { useRef } from 'react';
import toast from 'react-hot-toast';
import { FiUpload } from 'react-icons/fi';

import useDrivePicker from '../hooks/useGoogleDrivePicker';
import {
  uploadFiles,
  uploadFilesFromDrive,
  uploadImages,
} from '../services/api';
import {
  google_client_id,
  google_api_key,
  GOOGLE_DRIVE_PICKER_ENABLED,
} from '../utils/keys';

// Uploads files picked in the chat box: images to the image store, the rest
// as task files. Shared by the paperclip and the attach menu.
export const useChatFileUpload = ({
  userGoogleId,
  task_id,
  setJustSavedFiles,
  setTempImages,
  setJustUploadedImages,
}) => {
  const handleFileChange = async (event) => {
    const selected = Array.from(event.target.files);
    // Let the same file be chosen again next time.
    event.target.value = '';

    if (selected.length === 0) return;
    if (selected.length > 10) {
      toast.error('You can select a maximum of 10 files at a time.');
      return;
    }

    const imagesToUpload = selected.filter((file) =>
      file.type.startsWith('image/'),
    );
    const otherFilesToUpload = selected.filter(
      (file) => !file.type.startsWith('image/'),
    );

    // FIX: Create temporary image placeholders for instant UI feedback
    if (imagesToUpload.length > 0) {
      const newTempImages = imagesToUpload.map((file) => ({
        id: Date.now() + Math.random(),
        url: URL.createObjectURL(file),
        uploading: true,
      }));
      setTempImages(newTempImages);
    }

    try {
      if (imagesToUpload.length > 0) {
        const imageData = await uploadImages(
          userGoogleId,
          task_id,
          imagesToUpload,
        );
        if (imageData.error) {
          toast.error('Failed to upload some images.');
        } else {
          setJustUploadedImages(true); // Signal ImageComponent to refresh
        }
      }

      if (otherFilesToUpload.length > 0) {
        const fileData = await uploadFiles(
          userGoogleId,
          task_id,
          otherFilesToUpload,
        );
        if (fileData && !fileData.error) {
          setJustSavedFiles(true);
        } else {
          toast.error('Failed to upload some files.');
        }
      }

      if (selected.length > 0) {
        toast.success('Files uploaded successfully!');
      }
    } catch (error) {
      toast.error('An error occurred during upload.');
    } finally {
      setTempImages([]); // Clear temporary images after completion
    }
  };

  return handleFileChange;
};

const FileManagement = ({
  userGoogleId,
  setJustSavedFiles,
  setTempImages,
  setJustUploadedImages,
  task_id,
  onDone = () => {},
}) => {
  const fileInputRef = useRef(null);
  const uploadPicked = useChatFileUpload({
    userGoogleId,
    task_id,
    setJustSavedFiles,
    setTempImages,
    setJustUploadedImages,
  });
  const handleFileChange = (event) => {
    onDone();
    return uploadPicked(event);
  };

  const [openPicker] = useDrivePicker();
  const handleOpenPicker = () => {
    onDone();
    openPicker({
      clientId: google_client_id,
      developerKey: google_api_key,
      viewId: 'DOCS',
      // token: token, // pass oauth token in case you already have one
      showUploadView: true,
      showUploadFolders: true,
      supportDrives: true,
      multiselect: true,
      // customViews: customViewsArray, // custom view
      callbackFunction: async (data) => {
        if (data.action === 'cancel') {
        }
        if (data.action === 'picked') {
          // Extract file IDs from the picker response
          const fileIds = data.docs.map((doc) => doc.id);
          try {
            const responseData = await uploadFilesFromDrive(
              userGoogleId,
              task_id,
              fileIds,
            );
            if (responseData.error) {
            } else {
              setJustSavedFiles(true);
            }
          } catch (e) {
            toast.error('File upload failed');
          }
        }
      },
    });
  };

  return (
    <div className="w-64 rounded-xl border border-zinc-800 bg-zinc-900 p-1 text-zinc-200 shadow-xl">
      <button
        type="button"
        onClick={() => fileInputRef.current?.click()}
        className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left transition-colors hover:bg-zinc-800"
      >
        <FiUpload size={16} className="flex-shrink-0 text-zinc-400" />
        <span className="flex flex-col">
          <span className="text-sm">Upload from computer</span>
          <span className="text-xs text-zinc-500">
            Images or documents, up to 10
          </span>
        </span>
      </button>
      <input
        ref={fileInputRef}
        type="file"
        multiple
        onChange={handleFileChange}
        className="hidden"
      />
      {GOOGLE_DRIVE_PICKER_ENABLED && (
        <button
          type="button"
          onClick={handleOpenPicker}
          className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm transition-colors hover:bg-zinc-800"
        >
          <img
            alt=""
            src="https://res.cloudinary.com/dsguoq6ad/image/upload/v1726229007/mentorEducationImages/lhupskidmwtibynwa0no.png"
            className="h-4 w-4 flex-shrink-0"
          />
          Add from Google Drive
        </button>
      )}
    </div>
  );
};

export default FileManagement;
